// Author: Ozy
// Product File Upload Handler
// Uploads customer images to Shopify CDN via SDOAC proxy
// Integrates with Image Editor for cropping and adjustments

class ProductFileUpload {
  constructor(fileInput, options = {}) {
    this.fileInput = fileInput;
    this.apiUrl = options.apiUrl || 'https://sdoac.seraphinagrey.com/api/upload-file';
    this.shopDomain = options.shopDomain;
    this.maxSize = options.maxSize || 30 * 1024 * 1024; // 30MB
    this.allowedTypes = options.allowedTypes || ['image/jpeg', 'image/png', 'image/heic', 'image/webp', 'image/gif'];
    
    this.previewContainer = null;
    this.hiddenUrlInput = null;
    this.hiddenEditDataInput = null;
    this.errorContainer = null;
    
    // Image requirements from data attributes OR window.imageRequirements fallback
    this.slot = parseInt(fileInput.dataset.uploadSlot) || 1;
    this.productId = fileInput.dataset.productId;
    this.productHandle = fileInput.dataset.productHandle;
    
    // Try data attributes first
    let hasReqFromData = !!(fileInput.dataset.requiredDpi);
    let reqFromData = hasReqFromData ? {
      dpi: parseFloat(fileInput.dataset.requiredDpi),
      widthInches: parseFloat(fileInput.dataset.requiredWidthInches),
      heightInches: parseFloat(fileInput.dataset.requiredHeightInches),
      minPixelWidth: parseInt(fileInput.dataset.minPixelWidth),
      minPixelHeight: parseInt(fileInput.dataset.minPixelHeight),
      aspectRatio: parseFloat(fileInput.dataset.aspectRatio)
    } : null;
    
    // Fallback to window.imageRequirements if data attributes not set
    if (!hasReqFromData && window.imageRequirements?.hasRequirements) {
      const slotReq = window.imageRequirements.slots?.find(s => s.slot === this.slot);
      if (slotReq) {
        console.log('[File Upload] Using window.imageRequirements fallback for slot', this.slot, slotReq);
        hasReqFromData = true;
        reqFromData = slotReq;
      }
    }
    
    // NEW: If still no requirements, check if the selected variant has requirements
    // This handles products where metafields are ONLY on variants, not on the product
    if (!hasReqFromData && window.imageRequirements?.variantOverrides) {
      const selectedVariantId = window.imageRequirements.selectedVariantId;
      const variantReq = window.imageRequirements.variantOverrides[selectedVariantId];
      
      if (variantReq && variantReq.dpi && variantReq.widthInches && variantReq.heightInches) {
        console.log('[File Upload] Using variant-level requirements for variant', selectedVariantId, variantReq);
        hasReqFromData = true;
        reqFromData = {
          dpi: variantReq.dpi,
          widthInches: variantReq.widthInches,
          heightInches: variantReq.heightInches,
          minPixelWidth: variantReq.minPixelWidth,
          minPixelHeight: variantReq.minPixelHeight,
          aspectRatio: variantReq.aspectRatio,
          allowCropRotate: variantReq.allowCropRotate || false,
          variantTitle: variantReq.variantTitle
        };
      }
    }
    
    this.hasRequirements = hasReqFromData;
    this.requirements = reqFromData;
    
    // Update the dynamic requirements display if we have variant-level requirements
    this.updateRequirementsDisplay();
    
    console.log('[File Upload] Initialized with requirements:', {
      slot: this.slot,
      hasRequirements: this.hasRequirements,
      requirements: this.requirements,
      fromDataAttr: !!(fileInput.dataset.requiredDpi),
      fromWindowObj: !!(window.imageRequirements?.hasRequirements)
    });
    
    // Property names for cart (read from data attributes set by Liquid)
    this.propertyName = fileInput.dataset.propertyName || 'properties[_image_url]';
    this.originalUrlName = fileInput.dataset.originalUrlName || 'properties[_original_image_url]';
    this.editDataName = fileInput.dataset.editDataName || 'properties[_edit_data]';
    
    console.log('[File Upload] Property names:', {
      propertyName: this.propertyName,
      originalUrlName: this.originalUrlName,
      editDataName: this.editDataName
    });
    
    this.init();
  }

  init() {
    // Create preview container
    this.previewContainer = document.createElement('div');
    this.previewContainer.className = 'product-file-upload__preview';
    this.previewContainer.style.display = 'none';
    this.fileInput.parentNode.insertBefore(this.previewContainer, this.fileInput.nextSibling);

    // Find existing hidden inputs from Liquid template, or create new ones
    const uploadSlot = this.fileInput.closest('.product-custom-options__upload-slot') || 
                       this.fileInput.closest('.product-custom-options__option--file');
    
    this.hiddenUrlInput = uploadSlot?.querySelector('.product-custom-options__image-url') ||
                          uploadSlot?.querySelector(`input[name="${this.propertyName}"]`);
    
    this.hiddenOriginalUrlInput = uploadSlot?.querySelector('.product-custom-options__original-image-url') ||
                                  uploadSlot?.querySelector(`input[name="${this.originalUrlName}"]`);
    
    this.hiddenEditDataInput = uploadSlot?.querySelector('.product-custom-options__edit-data') ||
                               uploadSlot?.querySelector(`input[name="${this.editDataName}"]`);
    
    // Create hidden inputs if they don't exist (fallback)
    if (!this.hiddenUrlInput) {
      this.hiddenUrlInput = document.createElement('input');
      this.hiddenUrlInput.type = 'hidden';
      this.hiddenUrlInput.name = this.propertyName;
      this.fileInput.parentNode.insertBefore(this.hiddenUrlInput, this.fileInput.nextSibling);
      console.log('[File Upload] Created hidden URL input:', this.propertyName);
    } else {
      console.log('[File Upload] Found existing hidden URL input:', this.hiddenUrlInput.name);
    }

    if (!this.hiddenOriginalUrlInput) {
      this.hiddenOriginalUrlInput = document.createElement('input');
      this.hiddenOriginalUrlInput.type = 'hidden';
      this.hiddenOriginalUrlInput.name = this.originalUrlName;
      this.fileInput.parentNode.insertBefore(this.hiddenOriginalUrlInput, this.fileInput.nextSibling);
      console.log('[File Upload] Created hidden original URL input:', this.originalUrlName);
    } else {
      console.log('[File Upload] Found existing hidden original URL input:', this.hiddenOriginalUrlInput.name);
    }

    if (!this.hiddenEditDataInput) {
      this.hiddenEditDataInput = document.createElement('input');
      this.hiddenEditDataInput.type = 'hidden';
      this.hiddenEditDataInput.name = this.editDataName;
      this.fileInput.parentNode.insertBefore(this.hiddenEditDataInput, this.fileInput.nextSibling);
      console.log('[File Upload] Created hidden edit data input:', this.editDataName);
    } else {
      console.log('[File Upload] Found existing hidden edit data input:', this.hiddenEditDataInput.name);
    }

    // Create error container
    this.errorContainer = document.createElement('div');
    this.errorContainer.className = 'product-file-upload__error';
    this.errorContainer.style.display = 'none';
    this.fileInput.parentNode.insertBefore(this.errorContainer, this.fileInput.nextSibling);

    // Store original file input parent for later reference
    this.fileInputWrapper = this.fileInput.parentNode;

    // Listen for file selection
    this.fileInput.addEventListener('change', (e) => this.handleFileSelect(e));
    
    // Listen for variant changes to update requirements dynamically
    this.setupVariantChangeListener();
    
    // Listen for form submission to ensure values are set
    const form = this.fileInput.closest('form');
    if (form) {
      form.addEventListener('submit', (e) => {
        console.log('[File Upload] Form submitting. Hidden inputs:', {
          urlInput: this.hiddenUrlInput?.name,
          urlValue: this.hiddenUrlInput?.value,
          originalUrlInput: this.hiddenOriginalUrlInput?.name,
          originalUrlValue: this.hiddenOriginalUrlInput?.value,
          editInput: this.hiddenEditDataInput?.name,
          editValue: this.hiddenEditDataInput?.value
        });
        
        // Ensure values are set
        if (this.uploadedCdnUrl && this.hiddenUrlInput) {
          this.hiddenUrlInput.value = this.uploadedCdnUrl;
        }
        if (this.originalCdnUrl && this.hiddenOriginalUrlInput) {
          this.hiddenOriginalUrlInput.value = this.originalCdnUrl;
        }
      });
    }
    
    // Check for returned edit data from editor
    this.checkForEditData();
    
    // Disable Add to Cart on initial load if:
    // 1. File upload has image requirements (needs editor confirmation)
    // 2. No image has been uploaded and confirmed yet
    // This runs after checkForEditData, so if there's existing data it will have already enabled the button
    if (this.hasRequirements && !this.uploadedCdnUrl) {
      console.log('[File Upload] File upload with requirements but no image - disabling Add to Cart');
      this.setAddToCartEnabled(false, 'Please upload and crop your image before adding to cart.');
    }
  }

  /**
   * Update the requirements display text in the UI
   * This handles both product-level and variant-level requirements
   */
  updateRequirementsDisplay() {
    // Find the requirements element for this slot
    const uploadSlot = this.fileInput.closest('.product-custom-options__upload-slot');
    if (!uploadSlot) return;
    
    const reqElement = uploadSlot.querySelector('.product-custom-options__file-requirements');
    if (!reqElement) return;
    
    if (this.hasRequirements && this.requirements) {
      const req = this.requirements;
      const reqText = `Required: ${req.widthInches}" × ${req.heightInches}" at ${req.dpi} DPI`;
      const minText = `(min ${req.minPixelWidth} × ${req.minPixelHeight} px)`;
      
      reqElement.innerHTML = `${reqText} <span class="product-custom-options__min-resolution">${minText}</span>`;
      reqElement.style.display = '';
      
      console.log('[File Upload] Updated requirements display:', reqText);
    } else {
      // Hide the dynamic element if no requirements
      if (reqElement.classList.contains('product-custom-options__file-requirements--dynamic')) {
        reqElement.style.display = 'none';
      }
    }
  }

  /**
   * Set up listener for variant changes to update requirements dynamically
   */
  setupVariantChangeListener() {
    // Listen for variant-selects change events (Shopify Dawn theme pattern)
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      variantSelects.addEventListener('change', () => {
        this.handleVariantChange();
      });
    }
    
    // Also listen for the variant ID hidden input change (fallback)
    const form = this.fileInput.closest('form');
    if (form) {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) {
        // Use MutationObserver to detect value changes
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
              this.handleVariantChange();
            }
          });
        });
        observer.observe(variantInput, { attributes: true });
        
        // Also listen for direct changes
        variantInput.addEventListener('change', () => this.handleVariantChange());
      }
    }
    
    // Listen for custom variant change event (some themes use this)
    document.addEventListener('variant:changed', (e) => {
      this.handleVariantChange(e.detail?.variant?.id);
    });
    
    console.log('[File Upload] Variant change listener set up');
  }

  /**
   * Handle variant change - update requirements and re-validate
   */
  handleVariantChange(variantId = null) {
    // Get current variant ID
    if (!variantId) {
      const form = this.fileInput.closest('form');
      const variantInput = form?.querySelector('input[name="id"]');
      variantId = variantInput?.value;
    }
    
    if (!variantId) {
      console.log('[File Upload] No variant ID found');
      return;
    }
    
    // Debounce: Skip if we just processed this variant ID
    if (this._lastVariantId === variantId && this._lastVariantChangeTime && 
        Date.now() - this._lastVariantChangeTime < 500) {
      console.log('[File Upload] Skipping duplicate variant change event for:', variantId);
      return;
    }
    this._lastVariantId = variantId;
    this._lastVariantChangeTime = Date.now();
    
    console.log('[File Upload] Variant changed to:', variantId);
    
    // Store previous requirements for comparison
    const previousRequirements = this.requirements ? { ...this.requirements } : null;
    
    // Check for variant-specific overrides
    const overrides = window.imageRequirements?.variantOverrides?.[variantId];
    
    if (overrides && (overrides.dpi || overrides.widthInches || overrides.heightInches)) {
      // Use variant-specific requirements
      this.requirements = {
        dpi: overrides.dpi,
        widthInches: overrides.widthInches,
        heightInches: overrides.heightInches,
        minPixelWidth: overrides.minPixelWidth,
        minPixelHeight: overrides.minPixelHeight,
        aspectRatio: overrides.aspectRatio,
        allowCropRotate: overrides.allowCropRotate || false,
        variantTitle: overrides.variantTitle
      };
      this.hasRequirements = true;
      console.log('[File Upload] Using variant-specific requirements:', this.requirements);
    } else if (window.imageRequirements?.hasRequirements) {
      // Fall back to product-level requirements
      const slotReq = window.imageRequirements.slots?.find(s => s.slot === this.slot);
      if (slotReq) {
        // Use slot's allowCropRotate (from product metafield), variant override, or top-level fallback
        // Use nullish coalescing (??) to preserve explicit false values
        const allowCropRotate = overrides?.allowCropRotate ?? slotReq.allowCropRotate ?? window.imageRequirements.allowCropRotate ?? false;
        this.requirements = { 
          ...slotReq,
          allowCropRotate
        };
        this.hasRequirements = true;
        console.log('[File Upload] Using product-level requirements:', this.requirements);
      }
    } else {
      // No requirements
      this.hasRequirements = false;
      this.requirements = null;
      console.log('[File Upload] No requirements for this variant');
    }
    
    // Update window.imageRequirements.selectedVariantId
    if (window.imageRequirements) {
      window.imageRequirements.selectedVariantId = variantId;
    }
    
    // Always update the requirements display text
    this.updateRequirementsDisplay();
    
    // Re-validate and update UI if we have an uploaded image
    if (this.uploadedCdnUrl) {
      this.revalidateAndUpdateUI(previousRequirements);
    }
  }

  /**
   * Re-validate uploaded image against new requirements and update UI
   */
  revalidateAndUpdateUI(previousRequirements) {
    // Check if aspect ratio changed (crop invalidation)
    const previousAspect = previousRequirements?.aspectRatio;
    const newAspect = this.requirements?.aspectRatio;
    
    const aspectChanged = previousAspect && newAspect && 
      Math.abs(previousAspect - newAspect) / previousAspect > 0.05; // 5% tolerance
    
    // Check if we have existing edit data that would be invalidated
    const hasExistingCrop = this.editData && this.editData.edits && 
      (this.editData.edits.crop || this.editData.editedUrl !== this.editData.originalUrl);
    
    if (aspectChanged && hasExistingCrop) {
      console.log('[File Upload] Crop invalidated - aspect ratio changed from', previousAspect, 'to', newAspect);
      this._cropInvalidationShowing = true; // Flag to prevent overwriting
      this.showCropInvalidationWarning(previousRequirements);
    } else {
      // Just update the status display (but not if invalidation warning is showing)
      if (!this._cropInvalidationShowing) {
        this.updateStatusDisplay();
      }
    }
  }

  /**
   * Show warning that existing crop is no longer valid for new variant
   */
  showCropInvalidationWarning(previousRequirements) {
    // Set flag to prevent this warning from being overwritten
    this._cropInvalidationShowing = true;
    
    // Clear the edit data since crop is no longer valid
    this.editData = null;
    this.hiddenEditDataInput.value = '';
    
    // Revert to original image (uncropped)
    if (this.originalCdnUrl) {
      this.uploadedCdnUrl = this.originalCdnUrl;
      this.hiddenUrlInput.value = this.originalCdnUrl;
    }
    
    // Format dimension strings for display
    const prevDims = previousRequirements ? 
      `${previousRequirements.widthInches}" x ${previousRequirements.heightInches}"` : 'previous size';
    const newDims = this.requirements ?
      `${this.requirements.widthInches}" x ${this.requirements.heightInches}"` : 'new size';
    const variantName = this.requirements?.variantTitle || 'selected variant';
    
    // Update preview to show warning
    this.previewContainer.innerHTML = `
      <div class="product-file-upload__preview-content product-file-upload__preview-content--warning">
        <div class="product-file-upload__crop-warning">
          <div class="product-file-upload__crop-warning-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path d="M12 9V13M12 17H12.01M5.07 19H18.93C20.47 19 21.45 17.33 20.68 16L13.75 4C12.98 2.67 11.02 2.67 10.25 4L3.32 16C2.55 17.33 3.53 19 5.07 19Z" stroke="#f59e0b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="product-file-upload__crop-warning-text">
            <strong>Crop needs update</strong>
            <p>Your image was cropped for ${prevDims}, but you selected ${variantName} (${newDims}). Please re-edit your image.</p>
          </div>
        </div>
        <div class="product-file-upload__preview-row">
          <div class="product-file-upload__thumbnail product-file-upload__thumbnail--invalid">
            <img src="${this.uploadedCdnUrl}" alt="Image needs re-cropping" />
            <span class="product-file-upload__thumbnail-badge product-file-upload__thumbnail-badge--warning">Needs Update</span>
          </div>
          <div class="product-file-upload__info">
            <div class="product-file-upload__editor-prompt product-file-upload__editor-prompt--urgent">
              <button type="button" class="product-file-upload__editor-btn product-file-upload__editor-btn--urgent button">
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                  <path d="M11.5 2.5L13.5 4.5M1 15L1.5 12.5L12 2L14 4L3.5 14.5L1 15Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Re-edit Image
              </button>
            </div>
            <div class="product-file-upload__actions">
              <button type="button" class="product-file-upload__view-btn" aria-label="View image details">
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                  <path d="M1 8C1 8 3.5 3 8 3C12.5 3 15 8 15 8C15 8 12.5 13 8 13C3.5 13 1 8 1 8Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <circle cx="8" cy="8" r="2.5" stroke="currentColor" stroke-width="1.5"/>
                </svg>
                View
              </button>
              <button type="button" class="product-file-upload__remove-btn" aria-label="Remove file">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
    
    // Add button handlers
    const editorBtn = this.previewContainer.querySelector('.product-file-upload__editor-btn');
    if (editorBtn) {
      editorBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openEditor();
      });
    }
    
    const viewBtn = this.previewContainer.querySelector('.product-file-upload__view-btn');
    if (viewBtn) {
      viewBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.viewImage();
      });
    }
    
    const removeBtn = this.previewContainer.querySelector('.product-file-upload__remove-btn');
    if (removeBtn) {
      removeBtn.addEventListener('click', () => this.removeFile());
    }
  }

  /**
   * Update the status display without full re-render (for minor requirement changes)
   */
  updateStatusDisplay() {
    if (!this.uploadedCdnUrl) return;
    
    // Load image to get dimensions and re-validate
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const width = img.width;
      const height = img.height;
      
      // Re-render the preview with updated validation
      if (this.editData) {
        this.showEditedPreview(this.editData);
      } else {
        // Re-render unedited preview - simulate the showPreview result
        this.renderUpdatedPreview(width, height);
      }
    };
    img.src = this.uploadedCdnUrl;
  }

  /**
   * Render preview with current requirements (used after variant change)
   */
  renderUpdatedPreview(width, height) {
    const megapixels = (width * height) / 1000000;
    
    let passedValidation = megapixels >= 1.5;
    let validationMessage = `${width} × ${height} (${megapixels.toFixed(2)} MP)`;
    
    if (this.hasRequirements && this.requirements) {
      const aspectRatio = this.requirements.aspectRatio || (this.requirements.minPixelWidth / this.requirements.minPixelHeight);
      const imageAspect = width / height;
      
      let achievableW, achievableH;
      if (imageAspect > aspectRatio) {
        achievableH = height;
        achievableW = Math.round(height * aspectRatio);
      } else {
        achievableW = width;
        achievableH = Math.round(width / aspectRatio);
      }
      
      const meetsWidth = achievableW >= this.requirements.minPixelWidth;
      const meetsHeight = achievableH >= this.requirements.minPixelHeight;
      passedValidation = meetsWidth && meetsHeight;
      
      if (!passedValidation) {
        validationMessage = `Cropped: ${achievableW} × ${achievableH} (need ${this.requirements.minPixelWidth} × ${this.requirements.minPixelHeight})`;
      } else {
        validationMessage = `${width} × ${height} - Ready to crop`;
      }
    }
    
    const showEditorButton = this.hasRequirements;
    
    // Hide file input
    const inputLabel = this.fileInput.closest('label') || 
                       this.fileInput.parentElement.querySelector('label[for="' + this.fileInput.id + '"]');
    if (inputLabel) inputLabel.style.display = 'none';
    this.fileInput.style.display = 'none';
    
    this.previewContainer.innerHTML = `
      <div class="product-file-upload__preview-content">
        <div class="product-file-upload__preview-row">
          <div class="product-file-upload__thumbnail">
            <img src="${this.uploadedCdnUrl}" alt="Uploaded image preview" />
          </div>
          <div class="product-file-upload__info">
            <div class="product-file-upload__status ${passedValidation ? 'success' : 'warning'}">
              <svg width="24" height="24" viewBox="0 0 20 20" class="product-file-upload__status-icon">
                ${passedValidation 
                  ? '<path d="M7 10L9 12L13 8M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="#16a34a" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>'
                  : '<path d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="#f59e0b" stroke-width="2" fill="none" stroke-linecap="round"/>'
                }
              </svg>
              <div class="product-file-upload__status-text">
                <strong>${passedValidation ? 'Image Ready' : 'Low Resolution'}</strong>
                <span>${validationMessage}</span>
              </div>
            </div>
            ${showEditorButton ? `
              <div class="product-file-upload__editor-prompt">
                <p>Crop and adjust your image for the best print quality</p>
                <button type="button" class="product-file-upload__editor-btn button button--secondary">
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                    <path d="M11.5 2.5L13.5 4.5M1 15L1.5 12.5L12 2L14 4L3.5 14.5L1 15Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Open Editor
                </button>
              </div>
            ` : ''}
            <div class="product-file-upload__actions">
              <button type="button" class="product-file-upload__view-btn" aria-label="View image details">
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                  <path d="M1 8C1 8 3.5 3 8 3C12.5 3 15 8 15 8C15 8 12.5 13 8 13C3.5 13 1 8 1 8Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <circle cx="8" cy="8" r="2.5" stroke="currentColor" stroke-width="1.5"/>
                </svg>
                View
              </button>
              <button type="button" class="product-file-upload__remove-btn" aria-label="Remove file">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
    this.previewContainer.style.display = 'block';

    // Add button handlers
    const editorBtn = this.previewContainer.querySelector('.product-file-upload__editor-btn');
    if (editorBtn) {
      editorBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openEditor();
      });
    }

    const viewBtn = this.previewContainer.querySelector('.product-file-upload__view-btn');
    if (viewBtn) {
      viewBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.viewImage();
      });
    }

    const removeBtn = this.previewContainer.querySelector('.product-file-upload__remove-btn');
    if (removeBtn) {
      removeBtn.addEventListener('click', () => this.removeFile());
    }
  }

  checkForEditData() {
    if (!this.productHandle) return;
    
    // Restore product form state (options, text fields) if returning from editor
    this.restoreProductFormState();
    
    // First check for edit data from editor (user applied changes)
    const editStorageKey = `imageEdit_${this.productHandle}_${this.slot}`;
    const editDataStr = sessionStorage.getItem(editStorageKey);
    
    if (editDataStr) {
      try {
        const editData = JSON.parse(editDataStr);
        console.log('[File Upload] Found edit data from editor:', editData);
        
        // Apply the edit data
        this.uploadedCdnUrl = editData.editedUrl || editData.originalUrl;
        this.originalCdnUrl = editData.originalUrl;
        this.editData = editData;
        
        // Store in hidden inputs
        this.hiddenUrlInput.value = this.uploadedCdnUrl;
        this.hiddenOriginalUrlInput.value = this.originalCdnUrl;
        this.hiddenEditDataInput.value = JSON.stringify(editData.edits || {});
        
        // Show preview with edited image
        this.showEditedPreview(editData);
        
        // Enable Add to Cart - user has confirmed in editor
        this.setAddToCartEnabled(true);
        
        // Clear from sessionStorage (one-time use)
        sessionStorage.removeItem(editStorageKey);
        
        // Also clear the upload state since we have edit data
        const uploadStateKey = `uploadState_${this.productHandle}_${this.slot}`;
        sessionStorage.removeItem(uploadStateKey);
        
        return; // Don't continue to check upload state
        
      } catch (e) {
        console.error('[File Upload] Failed to parse edit data:', e);
        sessionStorage.removeItem(editStorageKey);
      }
    }
    
    // Check for upload state (user navigated back without applying)
    const uploadStateKey = `uploadState_${this.productHandle}_${this.slot}`;
    const uploadStateStr = sessionStorage.getItem(uploadStateKey);
    
    if (uploadStateStr) {
      try {
        const uploadState = JSON.parse(uploadStateStr);
        console.log('[File Upload] Found upload state from navigation:', uploadState);
        
        // Restore upload state
        this.uploadedCdnUrl = uploadState.uploadedCdnUrl;
        this.originalCdnUrl = uploadState.originalCdnUrl;
        
        // Store in hidden inputs
        this.hiddenUrlInput.value = this.uploadedCdnUrl;
        this.hiddenOriginalUrlInput.value = this.originalCdnUrl;
        
        // Restore the preview
        this.restoreUploadPreview(uploadState);
        
        // Re-disable Add to Cart - user returned without confirming in editor
        this.setAddToCartEnabled(false, 'Please open the editor to crop and confirm your image before adding to cart.');
        
        // Clear from sessionStorage (one-time use)
        sessionStorage.removeItem(uploadStateKey);
        
      } catch (e) {
        console.error('[File Upload] Failed to parse upload state:', e);
        sessionStorage.removeItem(uploadStateKey);
      }
    }
  }
  
  restoreUploadPreview(uploadState) {
    // Restore requirements from saved state or window.imageRequirements
    if (uploadState.requirements) {
      this.requirements = uploadState.requirements;
      this.hasRequirements = true;
    } else if (window.imageRequirements?.hasRequirements) {
      const slotReq = window.imageRequirements.slots?.find(s => s.slot === this.slot);
      if (slotReq) {
        this.requirements = slotReq;
        this.hasRequirements = true;
      }
    }
    
    console.log('[File Upload] Restoring with requirements:', this.requirements);
    
    // Hide the file input
    const inputLabel = this.fileInput.closest('label') || 
                       this.fileInput.parentElement.querySelector('label[for="' + this.fileInput.id + '"]');
    if (inputLabel) inputLabel.style.display = 'none';
    this.fileInput.style.display = 'none';
    
    const inputWrapper = this.fileInput.closest('.field__input-wrapper, .field, .shopify-challenge__container');
    if (inputWrapper && inputWrapper !== this.fileInputWrapper) {
      this.originalWrapperDisplay = inputWrapper.style.display || '';
      inputWrapper.style.display = 'none';
    }
    
    // Create a simple preview - we need to re-analyze the image
    this.previewContainer.innerHTML = `
      <div class="product-file-upload__preview-content">
        <div class="product-file-upload__status">
          <svg class="spinner" width="20" height="20" viewBox="0 0 20 20">
            <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="2" opacity="0.3"></circle>
            <path d="M10 2 A8 8 0 0 1 18 10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path>
          </svg>
          <div class="product-file-upload__status-text">
            <strong>Restoring upload...</strong>
          </div>
        </div>
      </div>
    `;
    this.previewContainer.style.display = 'block';
    
    // Load image and show proper preview
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const width = img.width;
      const height = img.height;
      const megapixels = (width * height) / 1000000;
      
      // Validation check
      let passedValidation = megapixels >= 1.5;
      let validationMessage = `${width} × ${height} (${megapixels.toFixed(2)} MP)`;
      
      if (this.hasRequirements) {
        // Check if image can be cropped to required aspect ratio and still meet min resolution
        const aspectRatio = this.requirements.aspectRatio || (this.requirements.minPixelWidth / this.requirements.minPixelHeight);
        const imageAspect = width / height;
        
        // Calculate what crop dimensions would be achievable
        let achievableW, achievableH;
        if (imageAspect > aspectRatio) {
          // Image is wider - crop width, use full height
          achievableH = height;
          achievableW = Math.round(height * aspectRatio);
        } else {
          // Image is taller - crop height, use full width
          achievableW = width;
          achievableH = Math.round(width / aspectRatio);
        }
        
        const meetsWidth = achievableW >= this.requirements.minPixelWidth;
        const meetsHeight = achievableH >= this.requirements.minPixelHeight;
        passedValidation = meetsWidth && meetsHeight;
        
        if (!passedValidation) {
          validationMessage = `Cropped: ${achievableW} × ${achievableH} (need ${this.requirements.minPixelWidth} × ${this.requirements.minPixelHeight})`;
        } else {
          validationMessage = `${width} × ${height} - Ready to crop`;
        }
      }
      
      const showEditorButton = this.hasRequirements;
      
      this.previewContainer.innerHTML = `
        <div class="product-file-upload__preview-content">
          <div class="product-file-upload__preview-row">
            <div class="product-file-upload__thumbnail">
              <img src="${this.uploadedCdnUrl}" alt="Uploaded image preview" />
            </div>
            <div class="product-file-upload__info">
              <div class="product-file-upload__status ${passedValidation ? 'success' : 'warning'}">
                <svg width="24" height="24" viewBox="0 0 20 20" class="product-file-upload__status-icon">
                  ${passedValidation 
                    ? '<path d="M7 10L9 12L13 8M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="#16a34a" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>'
                    : '<path d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="#f59e0b" stroke-width="2" fill="none" stroke-linecap="round"/>'
                  }
                </svg>
                <div class="product-file-upload__status-text">
                  <strong>${passedValidation ? 'Image Ready' : 'Low Resolution'}</strong>
                  <span>${validationMessage}</span>
                </div>
              </div>
              ${showEditorButton ? `
                <div class="product-file-upload__editor-prompt">
                  <p>Crop and adjust your image for the best print quality</p>
                  <button type="button" class="product-file-upload__editor-btn button button--secondary">
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                      <path d="M11.5 2.5L13.5 4.5M1 15L1.5 12.5L12 2L14 4L3.5 14.5L1 15Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Open Editor
                  </button>
                </div>
              ` : ''}
              <div class="product-file-upload__actions">
                <button type="button" class="product-file-upload__view-btn" aria-label="View image details">
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                    <path d="M1 8C1 8 3.5 3 8 3C12.5 3 15 8 15 8C15 8 12.5 13 8 13C3.5 13 1 8 1 8Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <circle cx="8" cy="8" r="2.5" stroke="currentColor" stroke-width="1.5"/>
                  </svg>
                  View
                </button>
                <button type="button" class="product-file-upload__remove-btn" aria-label="Remove file">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      `;
      
      // Add editor button handler
      const editorBtn = this.previewContainer.querySelector('.product-file-upload__editor-btn');
      if (editorBtn) {
        editorBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openEditor();
        });
      }
      
      // Add view button handler
      const viewBtn = this.previewContainer.querySelector('.product-file-upload__view-btn');
      if (viewBtn) {
        viewBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.viewImage();
        });
      }
      
      // Add remove button handler
      const removeBtn = this.previewContainer.querySelector('.product-file-upload__remove-btn');
      if (removeBtn) {
        removeBtn.addEventListener('click', () => this.removeFile());
      }
    };
    
    img.onerror = () => {
      console.error('[File Upload] Failed to load image for preview restoration');
      this.previewContainer.innerHTML = `
        <div class="product-file-upload__preview-content">
          <div class="product-file-upload__status warning">
            <svg width="24" height="24" viewBox="0 0 20 20" class="product-file-upload__status-icon">
              <path d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="#f59e0b" stroke-width="2" fill="none" stroke-linecap="round"/>
            </svg>
            <div class="product-file-upload__status-text">
              <strong>Image Ready</strong>
              <span>Click View for details</span>
            </div>
          </div>
          <div class="product-file-upload__actions">
            <button type="button" class="product-file-upload__view-btn" aria-label="View image details">
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                <path d="M1 8C1 8 3.5 3 8 3C12.5 3 15 8 15 8C15 8 12.5 13 8 13C3.5 13 1 8 1 8Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <circle cx="8" cy="8" r="2.5" stroke="currentColor" stroke-width="1.5"/>
              </svg>
              View
            </button>
            <button type="button" class="product-file-upload__remove-btn" aria-label="Remove file">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>
      `;
      
      // Add handlers
      const viewBtn = this.previewContainer.querySelector('.product-file-upload__view-btn');
      if (viewBtn) {
        viewBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.viewImage();
        });
      }
      
      const removeBtn = this.previewContainer.querySelector('.product-file-upload__remove-btn');
      if (removeBtn) {
        removeBtn.addEventListener('click', () => this.removeFile());
      }
    };
    
    img.src = this.uploadedCdnUrl;
  }

  showEditedPreview(editData) {
    // Hide file input
    const inputLabel = this.fileInput.closest('label') || 
                       this.fileInput.parentElement.querySelector('label[for="' + this.fileInput.id + '"]');
    if (inputLabel) inputLabel.style.display = 'none';
    this.fileInput.style.display = 'none';
    
    const inputWrapper = this.fileInput.closest('.field__input-wrapper, .field, .shopify-challenge__container');
    if (inputWrapper && inputWrapper !== this.fileInputWrapper) {
      this.originalWrapperDisplay = inputWrapper.style.display || '';
      inputWrapper.style.display = 'none';
    }
    
    // Use edited URL for thumbnail - MUST prefer editedUrl over uploadedCdnUrl
    // because uploadedCdnUrl might still be the original before edits
    let thumbnailUrl = editData.editedUrl || this.uploadedCdnUrl || editData.originalUrl;
    
    // Add cache-busting parameter to ensure browser loads fresh edited image
    if (thumbnailUrl) {
      const cacheBuster = editData.timestamp ? new Date(editData.timestamp).getTime() : Date.now();
      thumbnailUrl += (thumbnailUrl.includes('?') ? '&' : '?') + 'v=' + cacheBuster;
    }
    
    // Update uploadedCdnUrl to the edited version for consistency
    if (editData.editedUrl) {
      this.uploadedCdnUrl = editData.editedUrl;
      console.log('[File Upload] Updated uploadedCdnUrl to edited version:', editData.editedUrl);
    }
    
    console.log('[File Upload] Thumbnail URL for edited preview:', thumbnailUrl);
    
    this.previewContainer.innerHTML = `
      <div class="product-file-upload__preview-content product-file-upload__preview-content--edited">
        <div class="product-file-upload__preview-row">
          <div class="product-file-upload__thumbnail product-file-upload__thumbnail--edited">
            <img src="${thumbnailUrl}" alt="Edited image preview" />
            <span class="product-file-upload__thumbnail-badge">Edited</span>
          </div>
          <div class="product-file-upload__info">
            <div class="product-file-upload__status success">
              <svg width="24" height="24" viewBox="0 0 20 20" class="product-file-upload__status-icon">
                <path d="M7 10L9 12L13 8M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="#16a34a" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <div class="product-file-upload__status-text">
                <strong>Image Edited & Ready</strong>
                <span>Crop and adjustments applied</span>
              </div>
            </div>
            <div class="product-file-upload__actions">
              <button type="button" class="product-file-upload__edit-btn" aria-label="Edit image">
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                  <path d="M11.5 2.5L13.5 4.5M1 15L1.5 12.5L12 2L14 4L3.5 14.5L1 15Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Edit
              </button>
              <button type="button" class="product-file-upload__view-btn" aria-label="View image details">
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                  <path d="M1 8C1 8 3.5 3 8 3C12.5 3 15 8 15 8C15 8 12.5 13 8 13C3.5 13 1 8 1 8Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <circle cx="8" cy="8" r="2.5" stroke="currentColor" stroke-width="1.5"/>
                </svg>
                View
              </button>
              <button type="button" class="product-file-upload__remove-btn" aria-label="Remove file">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
    this.previewContainer.style.display = 'block';

    // Add button handlers
    const editBtn = this.previewContainer.querySelector('.product-file-upload__edit-btn');
    if (editBtn) {
      editBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openEditor();
      });
    }

    const viewBtn = this.previewContainer.querySelector('.product-file-upload__view-btn');
    if (viewBtn) {
      viewBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.viewImage();
      });
    }

    const removeBtn = this.previewContainer.querySelector('.product-file-upload__remove-btn');
    if (removeBtn) {
      removeBtn.addEventListener('click', () => this.removeFile());
    }
  }

  async handleFileSelect(event) {
    const file = event.target.files[0];
    
    if (!file) {
      this.clearPreview();
      return;
    }

    // Validate file
    const validation = this.validateFile(file);
    if (!validation.valid) {
      this.showError(validation.error);
      this.clearPreview();
      return;
    }

    // Show loading state
    this.showLoading();
    this.hideError();

    try {
      // Upload file to Shopify CDN
      const result = await this.uploadFile(file);
      
      // Store CDN URL in hidden fields (both edited and original start as the same)
      this.hiddenUrlInput.value = result.cdn_url;
      this.hiddenOriginalUrlInput.value = result.cdn_url;
      
      // Clear the file input - we don't want to submit the File object to cart
      // The CDN URL in the hidden field is all we need
      this.fileInput.value = '';
      
      // Show preview
      this.showPreview(file, result);
      
    } catch (error) {
      console.error('File upload error:', error);
      this.showError(error.message || 'Failed to upload file. Please try again.');
      this.clearPreview();
    }
  }

  validateFile(file) {
    // Check file type
    if (!this.allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: `Invalid file type. Allowed types: JPEG, PNG, HEIC, WebP, GIF. You selected: ${file.type}`
      };
    }

    // Check file size
    if (file.size > this.maxSize) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      const maxSizeMB = (this.maxSize / (1024 * 1024)).toFixed(0);
      return {
        valid: false,
        error: `File too large (${sizeMB}MB). Maximum size: ${maxSizeMB}MB`
      };
    }

    return { valid: true };
  }

  async uploadFile(file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('shop', this.shopDomain);

    const response = await fetch(this.apiUrl, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Upload failed');
    }

    return await response.json();
  }

  showLoading() {
    this.previewContainer.innerHTML = `
      <div class="product-file-upload__loading">
        <svg class="spinner" width="20" height="20" viewBox="0 0 20 20">
          <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="2" opacity="0.3"></circle>
          <path d="M10 2 A8 8 0 0 1 18 10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path>
        </svg>
        <span>Uploading...</span>
      </div>
    `;
    this.previewContainer.style.display = 'block';
  }

  showPreview(file, result) {
    // Hide the file input (Choose File button) after successful upload
    const inputLabel = this.fileInput.closest('label') || 
                       this.fileInput.parentElement.querySelector('label[for="' + this.fileInput.id + '"]');
    
    if (inputLabel) {
      inputLabel.style.display = 'none';
    }
    this.fileInput.style.display = 'none';
    
    // Also hide any wrapper divs that might contain the file input
    const inputWrapper = this.fileInput.closest('.field__input-wrapper, .field, .shopify-challenge__container');
    if (inputWrapper && inputWrapper !== this.fileInputWrapper) {
      this.originalWrapperDisplay = inputWrapper.style.display || '';
      inputWrapper.style.display = 'none';
    }
    
    // Store CDN URL for viewer
    this.uploadedCdnUrl = result.cdn_url;
    this.originalCdnUrl = result.cdn_url;
    
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const width = img.width;
        const height = img.height;
        const megapixels = (width * height) / 1000000;
        
        // Validation check against requirements or fallback to 1.5MP
        let passedValidation = megapixels >= 1.5;
        let validationMessage = `${width} × ${height} (${megapixels.toFixed(2)} MP)`;
        
        if (this.hasRequirements) {
          // Check if image can be cropped to required aspect ratio and still meet min resolution
          const aspectRatio = this.requirements.aspectRatio || (this.requirements.minPixelWidth / this.requirements.minPixelHeight);
          const imageAspect = width / height;
          
          // Calculate what crop dimensions would be achievable
          let achievableW, achievableH;
          if (imageAspect > aspectRatio) {
            // Image is wider - crop width, use full height
            achievableH = height;
            achievableW = Math.round(height * aspectRatio);
          } else {
            // Image is taller - crop height, use full width
            achievableW = width;
            achievableH = Math.round(width / aspectRatio);
          }
          
          const meetsWidth = achievableW >= this.requirements.minPixelWidth;
          const meetsHeight = achievableH >= this.requirements.minPixelHeight;
          passedValidation = meetsWidth && meetsHeight;
          
          if (!passedValidation) {
            validationMessage = `Cropped: ${achievableW} × ${achievableH} (need ${this.requirements.minPixelWidth} × ${this.requirements.minPixelHeight})`;
          } else {
            validationMessage = `${width} × ${height} - Ready to crop`;
          }
        }
        
        // Determine if we should show the editor button
        const showEditorButton = this.hasRequirements;
        
        this.previewContainer.innerHTML = `
          <div class="product-file-upload__preview-content">
            <div class="product-file-upload__preview-row">
              <div class="product-file-upload__thumbnail">
                <img src="${this.uploadedCdnUrl}" alt="Uploaded image preview" />
              </div>
              <div class="product-file-upload__info">
                <div class="product-file-upload__status ${passedValidation ? 'success' : 'warning'}">
                  <svg width="24" height="24" viewBox="0 0 20 20" class="product-file-upload__status-icon">
                    ${passedValidation 
                      ? '<path d="M7 10L9 12L13 8M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="#16a34a" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>'
                      : '<path d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="#f59e0b" stroke-width="2" fill="none" stroke-linecap="round"/>'
                    }
                  </svg>
                  <div class="product-file-upload__status-text">
                    <strong>${passedValidation ? 'Image Ready' : 'Low Resolution'}</strong>
                    <span>${validationMessage}</span>
                  </div>
                </div>
                ${showEditorButton ? `
                  <div class="product-file-upload__editor-prompt">
                    <p>Crop and adjust your image for the best print quality</p>
                    <button type="button" class="product-file-upload__editor-btn button button--secondary">
                      <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                        <path d="M11.5 2.5L13.5 4.5M1 15L1.5 12.5L12 2L14 4L3.5 14.5L1 15Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                      Open Editor
                    </button>
                  </div>
                ` : ''}
                <div class="product-file-upload__actions">
                  <button type="button" class="product-file-upload__view-btn" aria-label="View image details">
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                      <path d="M1 8C1 8 3.5 3 8 3C12.5 3 15 8 15 8C15 8 12.5 13 8 13C3.5 13 1 8 1 8Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      <circle cx="8" cy="8" r="2.5" stroke="currentColor" stroke-width="1.5"/>
                    </svg>
                    View
                  </button>
                  <button type="button" class="product-file-upload__remove-btn" aria-label="Remove file">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        `;
        this.previewContainer.style.display = 'block';

        // Disable Add to Cart until editor confirmation
        this.setAddToCartEnabled(false, 'Please open the editor to crop and confirm your image before adding to cart.');

        // Add editor button handler
        const editorBtn = this.previewContainer.querySelector('.product-file-upload__editor-btn');
        if (editorBtn) {
          editorBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.openEditor();
          });
        }

        // Add view button handler
        const viewBtn = this.previewContainer.querySelector('.product-file-upload__view-btn');
        if (viewBtn) {
          viewBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.viewImage();
          });
        }

        // Add remove button handler
        const removeBtn = this.previewContainer.querySelector('.product-file-upload__remove-btn');
        if (removeBtn) {
          removeBtn.addEventListener('click', () => this.removeFile());
        }
      };
      
      img.src = e.target.result;
    };

    reader.readAsDataURL(file);
  }

  openEditor() {
    // Clear crop invalidation flag since user is re-editing
    this._cropInvalidationShowing = false;
    
    // ALWAYS use original URL for editing, not the edited URL
    const imageUrl = this.originalCdnUrl || this.uploadedCdnUrl;
    
    console.log('[File Upload] Opening editor for ORIGINAL image:', imageUrl);
    console.log('[File Upload] uploadedCdnUrl (edited):', this.uploadedCdnUrl);
    console.log('[File Upload] originalCdnUrl:', this.originalCdnUrl);
    console.log('[File Upload] hasRequirements:', this.hasRequirements);
    console.log('[File Upload] requirements:', this.requirements);
    console.log('[File Upload] productHandle:', this.productHandle);
    console.log('[File Upload] slot:', this.slot);
    console.log('[File Upload] editData:', this.editData);
    
    // Save product form state (options, text fields) before navigating to editor
    this.saveProductFormState();
    
    // Save current upload state to sessionStorage so it survives navigation
    const uploadState = {
      uploadedCdnUrl: this.uploadedCdnUrl,
      originalCdnUrl: this.originalCdnUrl,
      hasRequirements: this.hasRequirements,
      requirements: this.requirements,
      slot: this.slot,
      productHandle: this.productHandle,
      editData: this.editData
    };
    const stateKey = `uploadState_${this.productHandle}_${this.slot}`;
    sessionStorage.setItem(stateKey, JSON.stringify(uploadState));
    console.log('[File Upload] Saved upload state to sessionStorage:', stateKey);
    
    // Build editor URL with parameters - ALWAYS use original URL
    const params = new URLSearchParams({
      image: imageUrl,
      product: this.productHandle || '',
      slot: this.slot,
      return: window.location.href
    });
    
    // Add requirements if available
    if (this.requirements) {
      const reqJson = JSON.stringify(this.requirements);
      console.log('[File Upload] Adding requirements to URL:', reqJson);
      params.set('requirements', reqJson);
    } else {
      console.log('[File Upload] No requirements to pass');
    }
    
    // Add existing edit data to restore state in editor
    if (this.editData && this.editData.edits) {
      const editDataJson = JSON.stringify(this.editData.edits);
      console.log('[File Upload] Adding edit data to URL for state restore:', editDataJson);
      params.set('editData', editDataJson);
    }
    
    // Navigate to editor page
    const editorUrl = `/pages/image-editor?${params.toString()}`;
    console.log('[File Upload] Full editor URL:', editorUrl);
    
    window.location.href = editorUrl;
  }

  /**
   * Save product form state (options, text fields) to sessionStorage
   * Called before navigating to image editor
   */
  saveProductFormState() {
    const form = this.fileInput.closest('form');
    if (!form) return;
    
    const formState = {};
    
    // Save all radio button selections
    const radioGroups = new Set();
    form.querySelectorAll('input[type="radio"]').forEach(radio => {
      radioGroups.add(radio.name);
    });
    radioGroups.forEach(name => {
      const checked = form.querySelector(`input[type="radio"][name="${name}"]:checked`);
      if (checked) {
        formState[`radio_${name}`] = checked.value;
      }
    });
    
    // Save all checkbox states
    form.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      if (checkbox.name && !checkbox.name.startsWith('properties[_')) {
        formState[`checkbox_${checkbox.name}`] = checkbox.checked;
      }
    });
    
    // Save all text inputs (except hidden ones for image data)
    form.querySelectorAll('input[type="text"], input[type="number"], input[type="email"]').forEach(input => {
      if (input.name && !input.name.startsWith('properties[_') && input.value) {
        formState[`text_${input.name}`] = input.value;
      }
    });
    
    // Save all textareas
    form.querySelectorAll('textarea').forEach(textarea => {
      if (textarea.name && textarea.value) {
        formState[`textarea_${textarea.name}`] = textarea.value;
      }
    });
    
    // Save all select dropdowns
    form.querySelectorAll('select').forEach(select => {
      if (select.name && select.value) {
        formState[`select_${select.name}`] = select.value;
      }
    });
    
    // Store with product handle as key
    const stateKey = `productFormState_${this.productHandle}`;
    sessionStorage.setItem(stateKey, JSON.stringify(formState));
    console.log('[File Upload] Saved product form state:', formState);
  }
  
  /**
   * Restore product form state from sessionStorage
   * Called when returning from image editor
   */
  restoreProductFormState() {
    const stateKey = `productFormState_${this.productHandle}`;
    const stateStr = sessionStorage.getItem(stateKey);
    
    if (!stateStr) return;
    
    try {
      const formState = JSON.parse(stateStr);
      console.log('[File Upload] Restoring product form state:', formState);
      
      const form = this.fileInput.closest('form');
      if (!form) return;
      
      // Restore radio buttons
      Object.keys(formState).forEach(key => {
        if (key.startsWith('radio_')) {
          const name = key.replace('radio_', '');
          const radio = form.querySelector(`input[type="radio"][name="${name}"][value="${formState[key]}"]`);
          if (radio) {
            radio.checked = true;
            // Trigger change event in case there are listeners
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
      
      // Restore checkboxes
      Object.keys(formState).forEach(key => {
        if (key.startsWith('checkbox_')) {
          const name = key.replace('checkbox_', '');
          const checkbox = form.querySelector(`input[type="checkbox"][name="${name}"]`);
          if (checkbox) {
            checkbox.checked = formState[key];
            checkbox.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
      
      // Restore text inputs
      Object.keys(formState).forEach(key => {
        if (key.startsWith('text_')) {
          const name = key.replace('text_', '');
          const input = form.querySelector(`input[name="${name}"]`);
          if (input) {
            input.value = formState[key];
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
        }
      });
      
      // Restore textareas
      Object.keys(formState).forEach(key => {
        if (key.startsWith('textarea_')) {
          const name = key.replace('textarea_', '');
          const textarea = form.querySelector(`textarea[name="${name}"]`);
          if (textarea) {
            textarea.value = formState[key];
            textarea.dispatchEvent(new Event('input', { bubbles: true }));
          }
        }
      });
      
      // Restore selects
      Object.keys(formState).forEach(key => {
        if (key.startsWith('select_')) {
          const name = key.replace('select_', '');
          const select = form.querySelector(`select[name="${name}"]`);
          if (select) {
            select.value = formState[key];
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
      
      // Clear from sessionStorage (one-time use)
      sessionStorage.removeItem(stateKey);
      console.log('[File Upload] Product form state restored and cleared from storage');
      
    } catch (e) {
      console.error('[File Upload] Failed to restore product form state:', e);
      sessionStorage.removeItem(stateKey);
    }
  }

  clearPreview() {
    this.previewContainer.innerHTML = '';
    this.previewContainer.style.display = 'none';
    this.hiddenUrlInput.value = '';
    this.hiddenOriginalUrlInput.value = '';
    this.hiddenEditDataInput.value = '';
    this.fileInput.value = '';
    this.uploadedCdnUrl = null;
    this.originalCdnUrl = null;
    this.editData = null;
  }

  removeFile() {
    // Clear crop invalidation flag
    this._cropInvalidationShowing = false;
    
    this.clearPreview();
    this.hideError();
    
    // Re-enable Add to Cart when file is removed
    this.setAddToCartEnabled(true);
    
    // Restore the file input (Choose File button) and its wrappers
    this.fileInput.style.display = '';
    
    // Restore label visibility
    const inputLabel = this.fileInput.closest('label') || 
                       this.fileInput.parentElement.querySelector('label[for="' + this.fileInput.id + '"]');
    if (inputLabel) {
      inputLabel.style.display = '';
    }
    
    // Restore wrapper visibility
    const inputWrapper = this.fileInput.closest('.field__input-wrapper, .field, .shopify-challenge__container');
    if (inputWrapper && this.originalWrapperDisplay !== undefined) {
      inputWrapper.style.display = this.originalWrapperDisplay;
    }
  }

  viewImage() {
    console.log('[File Upload] View Details clicked');
    
    if (!this.uploadedCdnUrl) {
      console.error('[File Upload] No uploaded image URL available');
      return;
    }

    console.log('[File Upload] Dispatching open-image-viewer event with URL:', this.uploadedCdnUrl);
    console.log('[File Upload] Requirements being passed:', this.requirements);
    console.log('[File Upload] Edit data being passed:', this.editData);

    // Trigger the cart image viewer with requirements AND editData
    // editData contains the swapped requirements if user rotated crop orientation
    const viewerEvent = new CustomEvent('open-image-viewer', {
      detail: { 
        imageUrl: this.uploadedCdnUrl,
        requirements: this.requirements,
        hasRequirements: this.hasRequirements,
        originalUrl: this.originalCdnUrl,
        editData: this.editData
      },
      bubbles: true,
      cancelable: true
    });
    document.dispatchEvent(viewerEvent);
    
    console.log('[File Upload] Event dispatched successfully');
  }

  showError(message) {
    this.errorContainer.textContent = message;
    this.errorContainer.style.display = 'block';
    this.errorContainer.setAttribute('role', 'alert');
  }

  hideError() {
    this.errorContainer.style.display = 'none';
    this.errorContainer.textContent = '';
  }

  formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  setAddToCartEnabled(enabled, message = null) {
    const form = this.fileInput.closest('form');
    if (!form) return;
    
    const submitBtn = form.querySelector('[type="submit"][name="add"]');
    if (!submitBtn) return;
    
    if (enabled) {
      submitBtn.disabled = false;
      submitBtn.removeAttribute('aria-disabled');
      submitBtn.classList.remove('product-form__submit--disabled-upload');
      const msg = form.querySelector('.product-file-upload__cart-message');
      if (msg) msg.remove();
    } else {
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('product-form__submit--disabled-upload');
      if (message && !form.querySelector('.product-file-upload__cart-message')) {
        const msgEl = document.createElement('p');
        msgEl.className = 'product-file-upload__cart-message';
        msgEl.textContent = message;
        submitBtn.parentNode.insertBefore(msgEl, submitBtn.nextSibling);
      }
    }
  }
}

// Auto-initialize for file inputs with data-file-upload attribute
document.addEventListener('DOMContentLoaded', () => {
  const fileInputs = document.querySelectorAll('input[type="file"][data-file-upload]');
  
  fileInputs.forEach(input => {
    const shopDomain = input.dataset.shopDomain || window.Shopify?.shop;
    const apiUrl = input.dataset.uploadUrl || 'https://sdoac.seraphinagrey.com/api/upload-file';
    
    new ProductFileUpload(input, {
      shopDomain,
      apiUrl
    });
  });
});

// Export for manual initialization
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductFileUpload;
}


